/**
 * @fileoverview Parser de coordenadas DMS (Grados/Minutos/Segundos)
 * @module lib/coordinate/parsers/dms
 * 
 * Extraído de coordinateNormalizer.ts v2.12 como parte de TD-002.C
 * 
 * Formatos soportados (C1-C8):
 * - C1: 37°26'46.5"N (estándar ISO)
 * - C2: 37º26'46,5"N (español con º y coma)
 * - C3: 37 26 46.5 N (espacios como separadores)
 * - C4: N37°26'46.5" (prefijo cardinal)
 * - C5: 37°26.775'N (minutos decimales)
 * - C6: 37° 26' 46.5" N (espacios extra)
 * - C7: +37°26'46.5" (signo explícito)
 * - C8: 37:26:46.5 N (dos puntos)
 * 
 * @version 1.0.0
 * @date 2026-01-26
 */

import type { ResultadoDMS, PatronDetectado } from '../types';

// ============================================================================
// FUNCIONES AUXILIARES
// ============================================================================

/**
 * Normaliza símbolos de grados a formato estándar
 * Reemplaza variantes tipográficas por símbolos ASCII estándar
 */
function normalizarSimboloGrados(str: string): string {
  return str
    .replace(/[º˚⁰]/g, '°')
    .replace(/[´`'']/g, "'")
    .replace(/["„‟]/g, '"');
}

/**
 * Convierte grados, minutos y segundos a valor decimal
 */
function dmsADecimal(grados: number, minutos: number, segundos: number, esNegativo: boolean): number {
  const decimal = Math.abs(grados) + (minutos / 60) + (segundos / 3600);
  return esNegativo ? -decimal : decimal;
}

/**
 * Determina si la dirección/signo indica valor negativo
 * W (Oeste) y S (Sur) son negativos, también el signo '-'
 */
function direccionEsNegativa(direccion: string | null, signo: string | null): boolean {
  if (signo === '-') return true;
  if (!direccion) return false;
  const dir = direccion.toUpperCase();
  return dir === 'W' || dir === 'O' || dir === 'S';
}

/**
 * Determina si la dirección indica latitud o longitud
 * @returns true = latitud (N/S), false = longitud (E/W), null = desconocido
 */
function direccionEsLatitud(direccion: string | null): boolean | null {
  if (!direccion) return null;
  const dir = direccion.toUpperCase();
  if (dir === 'N' || dir === 'S') return true;
  if (dir === 'E' || dir === 'W' || dir === 'O') return false;
  return null;
}

// ============================================================================
// PARSER PRINCIPAL DMS
// ============================================================================

/**
 * Parser principal DMS - detecta y convierte formatos C1-C8
 * 
 * @param valor - Coordenada en formato DMS
 * @returns Objeto ResultadoDMS con valor decimal y metadata
 * 
 * @example
 * parseDMS("37°26'46.5\"N")  // { esValido: true, valorDecimal: 37.44625, ... }
 * parseDMS("N37°26'46.5\"")  // { esValido: true, valorDecimal: 37.44625, formatoDetectado: 'DMS_PREFIJO' }
 * parseDMS("37 26 46.5 N")   // { esValido: true, valorDecimal: 37.44625, formatoDetectado: 'DMS_ESPACIOS' }
 */
export function parseDMS(valor: string): ResultadoDMS {
  const resultado: ResultadoDMS = {
    esValido: false,
    valorDecimal: null,
    grados: null,
    minutos: null,
    segundos: null,
    direccion: null,
    esLatitud: null,
    formatoDetectado: null,
    warning: null
  };
  
  if (!valor || typeof valor !== 'string') {
    return resultado;
  }
  
  let v = valor.trim();
  v = normalizarSimboloGrados(v);
  v = v.replace(/,/g, '.');
  
  // Detectar signo explícito (+/-)
  let signoExplicito: string | null = null;
  if (v.startsWith('+') || v.startsWith('-')) {
    signoExplicito = v[0];
    v = v.substring(1).trim();
  }
  
  // Detectar dirección como prefijo (N37°...)
  let direccionPrefijo: string | null = null;
  const matchPrefijo = v.match(/^([NSEW]|[NSEOnseo])\s*/i);
  if (matchPrefijo) {
    direccionPrefijo = matchPrefijo[1].toUpperCase();
    if (direccionPrefijo === 'O') direccionPrefijo = 'W';
    v = v.substring(matchPrefijo[0].length);
  }
  
  // Patrones regex para cada formato
  const regexC1C6 = /^(\d+)\s*°\s*(\d+)\s*['′]\s*(\d+\.?\d*)\s*["″]?\s*([NSEWO])?$/i;
  const regexC3 = /^(\d+)\s+(\d+)\s+(\d+\.?\d*)\s*([NSEWO])?$/i;
  const regexC5 = /^(\d+)\s*°\s*(\d+\.?\d*)\s*['′]\s*([NSEWO])?$/i;
  const regexC8 = /^(\d+):(\d+):(\d+\.?\d*)\s*([NSEWO])?$/i;
  const regexC8b = /^(\d+):(\d+):(\d+\.?\d*)([NSEWO])$/i;
  
  let match: RegExpMatchArray | null;
  let grados: number = 0, minutos: number = 0, segundos: number = 0;
  let direccion: string | null = direccionPrefijo;
  let formatoBase: PatronDetectado = 'DESCONOCIDO';
  
  // Intentar C1/C6: 37°26'46.5"N
  match = v.match(regexC1C6);
  if (match) {
    grados = parseFloat(match[1]);
    minutos = parseFloat(match[2]);
    segundos = parseFloat(match[3]);
    direccion = direccion || (match[4] ? match[4].toUpperCase() : null);
    formatoBase = (v.match(/\s/g) || []).length > 2 ? 'DMS_ESPACIOS_EXTRA' : 'DMS_ESTANDAR';
  }
  
  // Intentar C3: 37 26 46.5 N
  if (!match) {
    match = v.match(regexC3);
    if (match) {
      grados = parseFloat(match[1]);
      minutos = parseFloat(match[2]);
      segundos = parseFloat(match[3]);
      direccion = direccion || (match[4] ? match[4].toUpperCase() : null);
      formatoBase = 'DMS_ESPACIOS';
    }
  }
  
  // Intentar C5: 37°26.775'N (minutos decimales)
  if (!match) {
    match = v.match(regexC5);
    if (match) {
      grados = parseFloat(match[1]);
      minutos = parseFloat(match[2]);
      segundos = 0;
      direccion = direccion || (match[3] ? match[3].toUpperCase() : null);
      formatoBase = 'DMS_MINUTOS_DEC';
    }
  }
  
  // Intentar C8: 37:26:46.5 N (dos puntos)
  if (!match) {
    match = v.match(regexC8) || v.match(regexC8b);
    if (match) {
      grados = parseFloat(match[1]);
      minutos = parseFloat(match[2]);
      segundos = parseFloat(match[3]);
      direccion = direccion || (match[4] ? match[4].toUpperCase() : null);
      formatoBase = 'DMS_DOS_PUNTOS';
    }
  }
  
  // Si no coincide ningún patrón
  if (!match) {
    return resultado;
  }
  
  // Ajustar formato según prefijo/signo detectado
  if (direccionPrefijo) {
    formatoBase = 'DMS_PREFIJO';
  }
  
  if (signoExplicito) {
    formatoBase = 'DMS_SIGNO';
  }
  
  // Normalizar 'O' a 'W'
  if (direccion === 'O') direccion = 'W';
  
  // Validar rangos — corrección automática overflow DMS (F2.01)
  if (grados > 180 || minutos >= 60 || segundos >= 60) {
    if (grados <= 180 && (minutos >= 60 || segundos >= 60)) {
      // Corrección in-place de overflow DMS
      const origMin = minutos;
      const origSec = segundos;
      let overflowType = '';

      // Paso 1: normalizar segundos ≥60
      if (segundos >= 60) {
        const extraMin = Math.floor(segundos / 60);
        segundos = Math.round((segundos % 60) * 10000) / 10000;
        minutos += extraMin;
        overflowType = 'seconds';
      }

      // Paso 2: normalizar minutos ≥60 (puede crecer por paso 1)
      if (minutos >= 60) {
        const extraDeg = Math.floor(minutos / 60);
        minutos = Math.round((minutos % 60) * 10000) / 10000;
        grados += extraDeg;
        overflowType = overflowType === 'seconds' ? 'cascade' : 'minutes';
      }

      // Verificar que la corrección dio rangos válidos
      if (grados <= 180 && minutos < 60 && segundos < 60) {
        resultado.warning = `DMS overflow corregido (${overflowType}): ${origMin >= 60 ? `min=${origMin}` : ''}${origSec >= 60 ? ` sec=${origSec}` : ''} → ${grados}°${minutos}'${segundos}"`;
      } else {
        resultado.warning = `Valores fuera de rango tras corrección: ${grados}° ${minutos}' ${segundos}"`;
        return resultado;
      }
    } else {
      resultado.warning = `Valores fuera de rango: ${grados}° ${minutos}' ${segundos}"`;
      return resultado;
    }
  }
  
  // Calcular valor decimal
  const esNeg = direccionEsNegativa(direccion, signoExplicito);
  const decimal = dmsADecimal(grados, minutos, segundos, esNeg);
  
  // Construir resultado
  resultado.esValido = true;
  resultado.valorDecimal = decimal;
  resultado.grados = grados;
  resultado.minutos = minutos;
  resultado.segundos = segundos;
  resultado.direccion = direccion;
  resultado.esLatitud = direccionEsLatitud(direccion);
  resultado.formatoDetectado = formatoBase;
  
  // Warnings para valores fuera de Andalucía
  if (resultado.esLatitud === true) {
    if (decimal < 36 || decimal > 38.5) {
      resultado.warning = `Latitud ${decimal.toFixed(4)}° fuera de Andalucía (36-38.5)`;
    }
  } else if (resultado.esLatitud === false) {
    if (decimal > -1 || decimal < -7.5) {
      resultado.warning = `Longitud ${decimal.toFixed(4)}° fuera de Andalucía (-7.5 a -1)`;
    }
  }
  
  return resultado;
}

// ============================================================================
// DETECTOR DMS
// ============================================================================

/**
 * Detecta si un valor está en formato DMS (Grados/Minutos/Segundos)
 * 
 * @param valor - Valor a analizar
 * @returns true si es un formato DMS válido
 * 
 * @example
 * esDMS("37°26'46.5\"N") // true
 * esDMS("439501")        // false
 */
export function esDMS(valor: string): boolean {
  return parseDMS(valor).esValido;
}
